import React from 'react';
import { Form, Select, Button, Input, TimePicker, Tooltip, Card, Tabs, Tag, Empty, Spin, Radio, Space, message, Timeline, Popconfirm, Modal } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    SaveOutlined,
    ClockCircleOutlined,
    BookOutlined,
    CoffeeOutlined,
    CalendarOutlined,
    ExclamationCircleOutlined,
    ClearOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTimetableScreen } from '@hooks/tenant/timetable/useTimetableScreen';
import {
    DashboardContainer,
    StudentsHeaderRow,
    StudentsHeaderLeft,
    StudentsHeaderTitle,
    StudentsHeaderSub,
    StudentsHeaderActions,
} from './style';
import styled from 'styled-components';

const FilterCard = styled(Card)`
    border-radius: 20px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(240, 74, 74, 0.08)')} !important;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1e1e1e' : '#ffffff')} !important;
    margin-bottom: 24px;
    box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.03);

    .ant-card-body {
        padding: 20px 24px;
    }
`;

const CalendarTableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1e1e1e' : '#ffffff')};
    border-radius: 20px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(240, 74, 74, 0.08)')};
    box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.05);
    margin-bottom: 24px;
`;

const StyledCalendarTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
`;

const Th = styled.th`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#fafafa')};
    border-bottom: 2px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(240, 74, 74, 0.08)')};
    padding: 12px;
    font-weight: 700;
    text-align: center;
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : '#1e293b')};

    &:first-child {
        text-align: left;
        padding-left: 24px;
        width: 150px;
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#242424' : '#fafafa')};
        border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(240, 74, 74, 0.08)')};
    }
`;

const Td = styled.td`
    padding: 12px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(240, 74, 74, 0.06)')};
    text-align: center;
    vertical-align: middle;

    &:first-child {
        text-align: left;
        padding-left: 24px;
        font-weight: 700;
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#242424' : '#fafafa')};
        border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(240, 74, 74, 0.08)')};
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : '#334155')};
    }
`;

const Tr = styled.tr`
    transition: all 0.2s ease;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(240, 74, 74, 0.01)')};
    }
`;

const OffRowCell = styled(Td)`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#181818' : '#f8fafc')} !important;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.3)' : '#94a3b8')};
    font-style: italic;
    font-weight: 600;
    font-size: 13px;
    text-align: center;
    letter-spacing: 0.05em;
`;

const BreakCell = styled.div`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(234, 179, 8, 0.08)' : '#fef9c3')};
    border: 1px dashed ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(234, 179, 8, 0.3)' : '#fef08a')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f59e0b' : '#a16207')};
    padding: 8px 12px;
    border-radius: 10px;
    font-weight: 700;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    min-height: 40px;
`;

const SubjectDropdown = styled(Select)`
    width: 100%;
    
    .ant-select-selector {
        border-radius: 10px !important;
        border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.1)' : '#e2e8f0')} !important;
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#272727' : '#ffffff')} !important;
        transition: all 0.2s ease !important;
        height: 40px !important;
        display: flex !important;
        align-items: center !important;
        
        &:hover {
            border-color: #F04A4A !important;
        }
    }
`;

const HeaderSlotWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 10px;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(240, 74, 74, 0.05)')};
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(240, 74, 74, 0.1)')};
    }
`;

const DAYS_MAP = [
    { key: 'mon', label: 'Monday' },
    { key: 'tue', label: 'Tuesday' },
    { key: 'wed', label: 'Wednesday' },
    { key: 'thu', label: 'Thursday' },
    { key: 'fri', label: 'Friday' },
    { key: 'sat', label: 'Saturday' },
    { key: 'sun', label: 'Sunday' },
];

export default function TenantTimetable() {
    const {
        loading,
        loadingTimetable,
        isSaving,
        campusOptions,
        filteredClasses,
        selectedCampusId,
        setSelectedCampusId,
        selectedClassId,
        setSelectedClassId,
        selectedCampus,
        selectedClass,
        classSubjects,
        draftTimetable,
        activeKeys,
        getCampusDayHours,
        addSlot,
        removeSlot,
        updateSlot,
        updateSlotSubject,
        handleSave,
    } = useTimetableScreen();

    // Modal state for Add/Edit periods & breaks
    const [slotModalOpen, setSlotModalOpen] = React.useState(false);
    const [modalMode, setModalMode] = React.useState('add'); // 'add' | 'edit'
    const [targetSlotId, setTargetSlotId] = React.useState('');
    const [formType, setFormType] = React.useState('period');
    const [formStartTime, setFormStartTime] = React.useState('08:00');
    const [formEndTime, setFormEndTime] = React.useState('08:40');
    const [formLabel, setFormLabel] = React.useState('');

    // Use first active day's slots as master structure
    const firstActiveDay = activeKeys[0] || 'mon';
    const masterSlots = draftTimetable[firstActiveDay] || [];
    const activeDayHours = getCampusDayHours(firstActiveDay);

    const parseTimeToMinutes = (t) => {
        if (!t) return 0;
        const [h, m] = t.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    const formatMinutesToTime = (mins) => {
        const h = String(Math.floor(mins / 60)).padStart(2, '0');
        const m = String(mins % 60).padStart(2, '0');
        return `${h}:${m}`;
    };

    const nextStartTime = React.useMemo(() => {
        if (!activeDayHours) return '08:00';
        if (!masterSlots.length) return activeDayHours.from;
        return masterSlots[masterSlots.length - 1].endTime;
    }, [masterSlots, activeDayHours]);

    const defaultEndTime = React.useMemo(() => {
        if (!activeDayHours) return '08:40';
        const startMins = parseTimeToMinutes(nextStartTime);
        const schoolEndMins = parseTimeToMinutes(activeDayHours.to);
        let endMins = startMins + 40;
        if (endMins > schoolEndMins) endMins = schoolEndMins;
        return formatMinutesToTime(endMins);
    }, [nextStartTime, activeDayHours]);

    const reachedClosingTime = React.useMemo(() => {
        if (!activeDayHours || !masterSlots.length) return false;
        const lastSlot = masterSlots[masterSlots.length - 1];
        const lastEnd = parseTimeToMinutes(lastSlot.endTime);
        const schoolEnd = parseTimeToMinutes(activeDayHours.to);
        return lastEnd >= schoolEnd;
    }, [masterSlots, activeDayHours]);

    const handleAddSlotClick = () => {
        setModalMode('add');
        setFormType('period');
        setFormStartTime(nextStartTime);
        setFormEndTime(defaultEndTime);
        setFormLabel('');
        setSlotModalOpen(true);
    };

    const handleEditSlotClick = (slot) => {
        setModalMode('edit');
        setTargetSlotId(slot.id);
        setFormType(slot.type);
        setFormStartTime(slot.startTime);
        setFormEndTime(slot.endTime);
        setFormLabel(slot.label || '');
        setSlotModalOpen(true);
    };

    const handleModalSave = () => {
        const start = parseTimeToMinutes(formStartTime);
        const end = parseTimeToMinutes(formEndTime);

        if (end <= start) {
            message.error('End time must be after start time.');
            return;
        }

        if (activeDayHours) {
            const schoolEnd = parseTimeToMinutes(activeDayHours.to);
            if (end > schoolEnd) {
                message.error(`End time cannot exceed school closing time (${activeDayHours.to}).`);
                return;
            }
            const schoolStart = parseTimeToMinutes(activeDayHours.from);
            if (start < schoolStart) {
                message.error(`Start time cannot be before school opening time (${activeDayHours.from}).`);
                return;
            }
        }

        if (modalMode === 'add') {
            addSlot({
                type: formType,
                startTime: formStartTime,
                endTime: formEndTime,
                label: formLabel
            });
            message.success('Slot added successfully.');
        } else {
            updateSlot(targetSlotId, {
                type: formType,
                startTime: formStartTime,
                endTime: formEndTime,
                label: formLabel
            });
            message.success('Slot updated successfully.');
        }
        setSlotModalOpen(false);
    };

    const formatTo12Hr = (timeStr) => {
        if (!timeStr) return '';
        const [hStr, mStr] = timeStr.split(':');
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayH = h % 12 === 0 ? 12 : h % 12;
        const displayM = String(m).padStart(2, '0');
        return `${String(displayH).padStart(2, '0')}:${displayM} ${ampm}`;
    };

    const handleModalDelete = () => {
        removeSlot(targetSlotId);
        message.success('Slot deleted successfully.');
        setSlotModalOpen(false);
    };

    const handleSubjectChange = (dayKey, slotId, subjectId) => {
        const oldSubjectId = draftTimetable[dayKey]?.find(s => s.id === slotId)?.subjectId || '';
        if (oldSubjectId === subjectId) return;

        Modal.confirm({
            title: 'Confirm Subject Assignment',
            content: 'Are you sure you want to change the subject for this period?',
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                updateSlotSubject(dayKey, slotId, subjectId);
            }
        });
    };

    if (loading) {
        return (
            <DashboardContainer>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <Spin size="large" tip="Loading campus & classes data..." />
                </div>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Time Table Management</StudentsHeaderTitle>
                    <StudentsHeaderSub>
                        Configure and schedule weekly timetables for school classes.
                    </StudentsHeaderSub>
                </StudentsHeaderLeft>
            </StudentsHeaderRow>

            <FilterCard>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                    <div style={{ minWidth: 220 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'rgba(15, 23, 42, 0.6)' }}>CAMPUS / SCHOOL</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Select Campus"
                            value={selectedCampusId}
                            onChange={setSelectedCampusId}
                            options={campusOptions}
                            allowClear
                        />
                    </div>
                    <div style={{ minWidth: 220 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'rgba(15, 23, 42, 0.6)' }}>CLASS</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={selectedCampusId ? "Select Class" : "Select Campus first"}
                            value={selectedClassId}
                            onChange={setSelectedClassId}
                            options={filteredClasses.map(c => ({ value: c.id, label: c.displayName || c.name }))}
                            disabled={!selectedCampusId}
                            allowClear
                        />
                    </div>
                </div>
            </FilterCard>

            {!selectedClassId ? (
                <Card style={{ borderRadius: 20, textAlign: 'center', padding: '60px 24px', border: '1px solid rgba(240, 74, 74, 0.08)' }}>
                    <Empty
                        image={<CalendarOutlined style={{ fontSize: 48, color: '#F04A4A' }} />}
                        description={
                            <span style={{ fontSize: 15, fontWeight: 600, color: 'rgba(15, 23, 42, 0.5)' }}>
                                {!selectedCampusId
                                    ? "Select a Campus and a Class from the dropdowns above to manage the timetable."
                                    : "Select a Class to start building or editing its weekly timetable."
                                }
                            </span>
                        }
                    />
                </Card>
            ) : loadingTimetable ? (
                <Card style={{ borderRadius: 20, textAlign: 'center', padding: '60px 24px', border: '1px solid rgba(240, 74, 74, 0.08)' }}>
                    <Spin tip="Loading class timetable..." />
                </Card>
            ) : activeKeys.length === 0 ? (
                <Card style={{ borderRadius: 20, textAlign: 'center', padding: '48px 24px', border: '1px solid rgba(240, 74, 74, 0.1)' }}>
                    <Empty
                        image={<ExclamationCircleOutlined style={{ fontSize: 40, color: '#faad14' }} />}
                        description={
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>School is closed on all days</div>
                                <div style={{ color: 'rgba(15, 23, 42, 0.5)', fontSize: 13 }}>
                                    Please set active operational days in the School Time Configuration page for {selectedCampus?.branchName}.
                                </div>
                            </div>
                        }
                    />
                </Card>
            ) : (
                <>
                    {/* Weekly Schedule Table Header controls */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Weekly Calendar Schedule</span>
                            {isSaving ? (
                                <Tag color="warning" icon={<Spin size="small" style={{ marginRight: 6 }} />}>Saving changes...</Tag>
                            ) : (
                                <Tag color="success">Saved Successfully</Tag>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={handleAddSlotClick}
                                disabled={reachedClosingTime}
                                style={{ height: 38, borderRadius: 10 }}
                            >
                                Add Period / Break
                            </Button>
                        </div>
                    </div>

                    {masterSlots.length === 0 ? (
                        <Card style={{ borderRadius: 20, textAlign: 'center', padding: '48px 24px', border: '1px solid rgba(240, 74, 74, 0.08)', marginBottom: 24 }}>
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div>
                                        <div style={{ fontWeight: 600, color: 'rgba(15, 23, 42, 0.45)', marginBottom: 12 }}>
                                            No periods or breaks configured for this class's weekly schedule.
                                        </div>
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={handleAddSlotClick}
                                            style={{ borderRadius: 8 }}
                                        >
                                            Configure 1st Period Timing
                                        </Button>
                                    </div>
                                }
                            />
                        </Card>
                    ) : (
                        <CalendarTableContainer>
                            <StyledCalendarTable>
                                <thead>
                                    <tr>
                                        <Th>Day</Th>
                                        {masterSlots.map((slot, idx) => {
                                            const isBreak = slot.type === 'break';
                                            return (
                                                <Th key={slot.id}>
                                                    <Tooltip title="Click to edit timings, type, or delete slot">
                                                        <HeaderSlotWrapper onClick={() => handleEditSlotClick(slot)}>
                                                            <span style={{ color: isBreak ? '#d97706' : '#F04A4A', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                {isBreak ? '☕ Break' : `📚 Period ${idx + 1}`}
                                                                <SettingOutlined style={{ fontSize: 10, opacity: 0.6 }} />
                                                            </span>
                                                            <span style={{ fontSize: 13, fontWeight: 700 }}>
                                                                {formatTo12Hr(slot.startTime)} - {formatTo12Hr(slot.endTime)}
                                                            </span>
                                                        </HeaderSlotWrapper>
                                                    </Tooltip>
                                                </Th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {DAYS_MAP.map((day) => {
                                        const hours = getCampusDayHours(day.key);
                                        const isOff = !hours;
                                        const daySlots = draftTimetable[day.key] || [];

                                        return (
                                            <Tr key={day.key}>
                                                <Td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <span style={{ fontWeight: 700, color: isOff ? '#94a3b8' : 'inherit' }}>{day.label}</span>
                                                        {!isOff ? (
                                                            <span style={{ fontSize: 10, color: 'rgba(15, 23, 42, 0.45)', fontWeight: 500 }}>
                                                                {formatTo12Hr(hours.from)} - {formatTo12Hr(hours.to)}
                                                            </span>
                                                        ) : (
                                                            <span style={{ fontSize: 10, color: '#F04A4A', fontWeight: 600 }}>CLOSED</span>
                                                        )}
                                                    </div>
                                                </Td>
                                                {isOff ? (
                                                    <OffRowCell colSpan={masterSlots.length}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                                            <ExclamationCircleOutlined />
                                                            <span>CAMPUS CLOSED / WEEKEND</span>
                                                        </div>
                                                    </OffRowCell>
                                                ) : (
                                                    daySlots.map((slot) => {
                                                        const isBreak = slot.type === 'break';
                                                        if (isBreak) {
                                                            const isFirstActiveDay = activeKeys[0] === day.key;
                                                            if (!isFirstActiveDay) {
                                                                return null;
                                                            }
                                                            return (
                                                                <Td key={slot.id} rowSpan={activeKeys.length} style={{ verticalAlign: 'middle', padding: 8 }}>
                                                                    <BreakCell style={{ minHeight: 120, height: 'calc(100% - 8px)' }}>
                                                                        <CoffeeOutlined style={{ fontSize: 18 }} />
                                                                        <span>{slot.label || 'Break'}</span>
                                                                    </BreakCell>
                                                                </Td>
                                                            );
                                                        }

                                                        return (
                                                            <Td key={slot.id}>
                                                                {classSubjects.length > 0 ? (
                                                                    <SubjectDropdown
                                                                        placeholder="Select Subject"
                                                                        value={slot.subjectId || undefined}
                                                                        onChange={(val) => handleSubjectChange(day.key, slot.id, val || '')}
                                                                        options={[
                                                                            { value: '', label: 'None (Free Period)' },
                                                                            ...classSubjects.map((s) => ({ value: s.id, label: s.name }))
                                                                        ]}
                                                                        allowClear
                                                                    />
                                                                ) : (
                                                                    <div style={{ color: '#d97706', fontSize: 11, fontWeight: 500 }}>
                                                                        No Subjects
                                                                    </div>
                                                                )}
                                                            </Td>
                                                        );
                                                    })
                                                )}
                                            </Tr>
                                        );
                                    })}
                                </tbody>
                            </StyledCalendarTable>
                        </CalendarTableContainer>
                    )}

                    {/* Period / Break Configuration Modal */}
                    <Modal
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ClockCircleOutlined style={{ color: '#F04A4A' }} />
                                <span style={{ fontWeight: 800 }}>
                                    {modalMode === 'add' ? 'Add Period / Break' : 'Configure Period / Break'}
                                </span>
                            </div>
                        }
                        open={slotModalOpen}
                        onOk={handleModalSave}
                        onCancel={() => setSlotModalOpen(false)}
                        okText={modalMode === 'add' ? 'Add Slot' : 'Save Changes'}
                        cancelText="Cancel"
                        destroyOnClose
                        footer={[
                            modalMode === 'edit' && (
                                <Popconfirm
                                    key="delete"
                                    title="Delete this slot?"
                                    description="This will remove this period/break from all days."
                                    onConfirm={handleModalDelete}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger type="dashed" style={{ float: 'left' }} icon={<DeleteOutlined />}>
                                        Delete Slot
                                    </Button>
                                </Popconfirm>
                            ),
                            <Button key="cancel" onClick={() => setSlotModalOpen(false)}>
                                Cancel
                            </Button>,
                            <Button key="submit" type="primary" onClick={handleModalSave} style={{ background: '#F04A4A', borderColor: '#F04A4A' }}>
                                {modalMode === 'add' ? 'Add Slot' : 'Save Changes'}
                            </Button>
                        ]}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'rgba(15, 23, 42, 0.6)' }}>SLOT TYPE</div>
                                <Radio.Group
                                    value={formType}
                                    onChange={(e) => setFormType(e.target.value)}
                                    buttonStyle="solid"
                                    style={{ width: '100%' }}
                                >
                                    <Radio.Button value="period" style={{ width: '50%', textAlign: 'center' }}>📚 Period</Radio.Button>
                                    <Radio.Button value="break" style={{ width: '50%', textAlign: 'center' }}>☕ Break</Radio.Button>
                                </Radio.Group>
                            </div>

                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'rgba(15, 23, 42, 0.6)' }}>START TIME</div>
                                    <Tooltip title={modalMode === 'edit' && targetSlotId !== masterSlots[0]?.id ? "Start time is auto-linked to the previous slot's end time" : ""}>
                                        <TimePicker
                                            format="hh:mm A"
                                            use12Hours
                                            value={dayjs(formStartTime, 'HH:mm')}
                                            disabled={modalMode === 'edit' ? targetSlotId !== masterSlots[0]?.id : masterSlots.length > 0}
                                            allowClear={false}
                                            style={{ width: '100%' }}
                                            onChange={(time) => {
                                                if (time) {
                                                    setFormStartTime(time.format('HH:mm'));
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'rgba(15, 23, 42, 0.6)' }}>END TIME</div>
                                    <TimePicker
                                        format="hh:mm A"
                                        use12Hours
                                        value={dayjs(formEndTime, 'HH:mm')}
                                        allowClear={false}
                                        style={{ width: '100%' }}
                                        onChange={(time) => {
                                            if (time) {
                                                setFormEndTime(time.format('HH:mm'));
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {formType === 'break' && (
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'rgba(15, 23, 42, 0.6)' }}>BREAK LABEL</div>
                                    <Input
                                        placeholder="e.g. Lunch Break, Recess, Tea Break"
                                        value={formLabel}
                                        onChange={(e) => setFormLabel(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    </Modal>
                </>
            )}
        </DashboardContainer>
    );
}
